/**
 * Terminal
 *
 * Displays streaming command output with ANSI styling.
 *
 * Primary responsibilities:
 * - Render terminal output with ANSI SGR colors and text styles
 * - Keep output scrolled to the latest line while streaming
 * - Provide copy and clear actions for command output
 *
 * Dependencies:
 * - components/ui/button
 */

'use client'

import {
  type ComponentProps,
  type CSSProperties,
  type ReactNode,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { Check, Copy, Loader2, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

type TerminalProps = {
  output: string
  isStreaming?: boolean
  autoScroll?: boolean
  onClear?: () => void
  className?: string
}

type TerminalCopyButtonProps = ComponentProps<typeof Button> & {
  text?: string
  onCopy?: () => void
  onError?: (error: Error) => void
  timeout?: number
}

type AnsiStyleState = {
  color?: string
  backgroundColor?: string
  bold?: boolean
  italic?: boolean
  underline?: boolean
}

const ANSI_PATTERN = /\x1b\[([0-9;]*)m/g
const ANSI_COLORS = [
  '#111827',
  '#dc2626',
  '#16a34a',
  '#ca8a04',
  '#2563eb',
  '#9333ea',
  '#0891b2',
  '#f9fafb'
]
const ANSI_BRIGHT_COLORS = [
  '#6b7280',
  '#ef4444',
  '#22c55e',
  '#eab308',
  '#3b82f6',
  '#a855f7',
  '#06b6d4',
  '#ffffff'
]

// --------------

function getAnsiColor(index: number) {
  if (index < 16) {
    return index < 8 ? ANSI_COLORS[index] : ANSI_BRIGHT_COLORS[index - 8]
  }

  if (index >= 16 && index <= 231) {
    const normalized = index - 16
    const red = Math.floor(normalized / 36)
    const green = Math.floor((normalized % 36) / 6)
    const blue = normalized % 6
    const toChannel = (value: number) => (value === 0 ? 0 : value * 40 + 55)
    return `rgb(${toChannel(red)}, ${toChannel(green)}, ${toChannel(blue)})`
  }

  if (index >= 232 && index <= 255) {
    const channel = (index - 232) * 10 + 8
    return `rgb(${channel}, ${channel}, ${channel})`
  }

  return undefined
}

// --------------

function toCssStyle(state: AnsiStyleState): CSSProperties {
  return {
    color: state.color,
    backgroundColor: state.backgroundColor,
    fontWeight: state.bold ? 700 : undefined,
    fontStyle: state.italic ? 'italic' : undefined,
    textDecorationLine: state.underline ? 'underline' : undefined
  }
}

// --------------

function pushSegment(
  segments: ReactNode[],
  text: string,
  state: AnsiStyleState,
  key: number
) {
  if (!text) return

  segments.push(
    <span key={`ansi-${key}`} style={toCssStyle(state)}>
      {text}
    </span>
  )
}

// --------------

function applyAnsiCodes(state: AnsiStyleState, codes: number[]) {
  let index = 0
  const nextState = { ...state }

  while (index < codes.length) {
    const code = codes[index] ?? 0

    if (code === 0) {
      nextState.color = undefined
      nextState.backgroundColor = undefined
      nextState.bold = false
      nextState.italic = false
      nextState.underline = false
    } else if (code === 1) {
      nextState.bold = true
    } else if (code === 3) {
      nextState.italic = true
    } else if (code === 4) {
      nextState.underline = true
    } else if (code === 22) {
      nextState.bold = false
    } else if (code === 23) {
      nextState.italic = false
    } else if (code === 24) {
      nextState.underline = false
    } else if (code >= 30 && code <= 37) {
      nextState.color = ANSI_COLORS[code - 30]
    } else if (code >= 90 && code <= 97) {
      nextState.color = ANSI_BRIGHT_COLORS[code - 90]
    } else if (code >= 40 && code <= 47) {
      nextState.backgroundColor = ANSI_COLORS[code - 40]
    } else if (code >= 100 && code <= 107) {
      nextState.backgroundColor = ANSI_BRIGHT_COLORS[code - 100]
    } else if (code === 39) {
      nextState.color = undefined
    } else if (code === 49) {
      nextState.backgroundColor = undefined
    } else if ((code === 38 || code === 48) && codes[index + 1] === 5) {
      const color = getAnsiColor(codes[index + 2] ?? -1)
      if (color) {
        if (code === 38) {
          nextState.color = color
        } else {
          nextState.backgroundColor = color
        }
      }
      index += 2
    } else if ((code === 38 || code === 48) && codes[index + 1] === 2) {
      const red = codes[index + 2]
      const green = codes[index + 3]
      const blue = codes[index + 4]
      if (
        typeof red === 'number' &&
        typeof green === 'number' &&
        typeof blue === 'number'
      ) {
        const color = `rgb(${red}, ${green}, ${blue})`
        if (code === 38) {
          nextState.color = color
        } else {
          nextState.backgroundColor = color
        }
      }
      index += 4
    }

    index += 1
  }

  return nextState
}

// --------------

function parseAnsiOutput(output: string) {
  const segments: ReactNode[] = []
  let state: AnsiStyleState = {}
  let lastIndex = 0
  let segmentIndex = 0

  for (const match of output.matchAll(ANSI_PATTERN)) {
    const matchIndex = match.index ?? 0
    pushSegment(
      segments,
      output.slice(lastIndex, matchIndex),
      state,
      segmentIndex++
    )

    const codes = (match[1] || '0')
      .split(';')
      .map((part) => Number.parseInt(part, 10))
      .filter((code) => Number.isFinite(code))

    state = applyAnsiCodes(state, codes.length > 0 ? codes : [0])
    lastIndex = matchIndex + match[0].length
  }

  pushSegment(segments, output.slice(lastIndex), state, segmentIndex)

  return segments
}

// --------------

export function TerminalHeader({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 border-b border-white/10 px-3 py-2',
        className
      )}
      {...props}
    />
  )
}

export function TerminalTitle({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('min-w-0 truncate text-xs font-medium text-zinc-100', className)}
      {...props}
    />
  )
}

export function TerminalStatus({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex items-center gap-1.5 text-[10px] text-zinc-400', className)}
      {...props}
    />
  )
}

export function TerminalActions({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('flex items-center gap-1.5', className)} {...props} />
}

export const TerminalContent = forwardRef<HTMLDivElement, ComponentProps<'div'>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('max-h-80 overflow-auto px-3 py-2', className)}
        data-terminal-output
        {...props}
      />
    )
  }
)

TerminalContent.displayName = 'TerminalContent'

function TerminalOutputPlaceholder() {
  return (
    <span className="text-zinc-500">
      Waiting for output...
    </span>
  )
}

export function TerminalCopyButton({
  text = '',
  onCopy,
  onError,
  timeout = 2000,
  className,
  ...props
}: TerminalCopyButtonProps) {
  const [copied, setCopied] = useState(false)

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      onCopy?.()
      window.setTimeout(() => setCopied(false), timeout)
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error('Copy failed'))
    }
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn('size-7 text-zinc-300 hover:bg-white/10 hover:text-white', className)}
      onClick={copyText}
      disabled={!text}
      aria-label={copied ? 'Copied' : 'Copy output'}
      {...props}
    >
      {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
    </Button>
  )
}

export function TerminalClearButton({
  className,
  ...props
}: ComponentProps<typeof Button>) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn('size-7 text-zinc-300 hover:bg-white/10 hover:text-white', className)}
      aria-label="Clear output"
      {...props}
    >
      <Trash2 className="size-3.5" />
    </Button>
  )
}

export function Terminal({
  output,
  isStreaming = false,
  autoScroll = true,
  onClear,
  className
}: TerminalProps) {
  const contentRef = useRef<HTMLDivElement | null>(null)
  const renderedOutput = useMemo(() => parseAnsiOutput(output), [output])

  useEffect(() => {
    if (!autoScroll || !contentRef.current) return
    contentRef.current.scrollTop = contentRef.current.scrollHeight
  }, [autoScroll, output])

  return (
    <div
      className={cn(
        'overflow-hidden rounded-md border border-zinc-800 bg-zinc-950 text-zinc-100 shadow-sm',
        className
      )}
    >
      <TerminalHeader>
        <TerminalTitle>Terminal</TerminalTitle>
        <div className="flex items-center gap-2">
          <TerminalStatus>
            {isStreaming ? (
              <>
                <Loader2 className="size-3 animate-spin" />
                Streaming
              </>
            ) : (
              'Output'
            )}
          </TerminalStatus>
          <TerminalActions>
            <TerminalCopyButton text={output} />
            {onClear ? <TerminalClearButton onClick={onClear} /> : null}
          </TerminalActions>
        </div>
      </TerminalHeader>
      <TerminalContent ref={contentRef}>
        <pre className="min-h-20 whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed">
          {renderedOutput.length > 0 ? renderedOutput : <TerminalOutputPlaceholder />}
          {isStreaming ? (
            <span className="ml-0.5 inline-block h-3 w-1.5 translate-y-0.5 animate-pulse bg-zinc-300" />
          ) : null}
        </pre>
      </TerminalContent>
    </div>
  )
}
